import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthLayout } from './AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ForgotPasswordSchema, type ForgotPasswordValues } from './schemas';
import { forgotPassword } from '@/api/auth';
import { applyServerErrors } from './parseApiError';

export function ForgotPasswordPage() {
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (values: ForgotPasswordValues) => {
    try {
      await forgotPassword(values.email);
      toast.success("If that email is registered, we've sent a reset link.");
      reset();
    } catch (err) {
      applyServerErrors(err, setError);
    }
  };

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We'll email you a single-use link, valid for 15 minutes."
      footer={
        <Link to="/login" className="font-medium text-[color:var(--kz-color-brand-primary)] hover:underline">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            {...register('email')}
          />
          {errors.email?.message && (
            <p className="text-xs text-[color:var(--kz-color-status-danger)]">{errors.email.message}</p>
          )}
        </div>

        {errors.root?.serverError?.message && (
          <p className="rounded-control border border-[color:var(--kz-color-status-danger)]/30 bg-[color:var(--kz-color-feedback-danger-bg)] p-3 text-sm text-[color:var(--kz-color-status-danger)]">
            {errors.root.serverError.message}
          </p>
        )}

        {isSubmitSuccessful && (
          <p className="rounded-control border border-[color:var(--kz-color-status-success)]/30 bg-[color:var(--kz-color-feedback-success-bg)] p-3 text-sm text-[color:var(--kz-color-status-success)]">
            Check your inbox.
          </p>
        )}

        <Button type="submit" size="lg" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="animate-spin" />
              Sending
            </>
          ) : (
            'Send reset link'
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
